import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, UserCircle, Calendar, BarChart3, PieChart,
  ArrowRight, ChevronRight, Briefcase, Bot, AlertTriangle,
  Database, Loader2
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { PageErrorBoundary } from '../components/ErrorBoundary';

// TENANT ISOLATION: Removed hardcoded DEMO_TENANT_ID - all tenants are treated equally

const ResourceCard = ({ title, description, icon: Icon, path, color }) => {
  const navigate = useNavigate();
  
  return (
    <div
      onClick={() => navigate(path)}
      className={`bg-white rounded-xl border p-6 cursor-pointer hover:shadow-lg transition-all group`}
      data-testid={`resource-nav-${path.split('/').pop()}`}
    >
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-6 h-6" />
        </div>
        <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-purple-600 group-hover:translate-x-1 transition-all" />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
        {title}
      </h3>
      <p className="mt-1 text-sm text-gray-500">{description}</p>
    </div>
  );
};

const ResourceLandingPage = () => {
  const navigate = useNavigate();
  const { tenantId: authTenantId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    roles: 0,
    teams: 0,
    allocations: 0,
    avgUtilisation: 0,
    conflicts: 0
  });

  // TENANT ISOLATION: Use impersonated tenant if sysadmin is impersonating, otherwise use auth tenant
  const tenantId = localStorage.getItem('impersonated_tenant_id') || authTenantId;

  // TENANT ISOLATION: All tenants treated equally - no special demo tenant
  const isDemoTenant = false;

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, teamsRes, allocsRes, conflictsRes] = await Promise.all([
        api.listRoles(tenantId, true),
        api.listResourceTeams(tenantId, true),
        api.listAllocations(tenantId),
        api.getResourceConflicts(tenantId, '2026-01', '2026-12')
      ]);
      
      const roles = rolesRes.data.roles || [];
      const teams = teamsRes.data.teams || [];
      const allocations = allocsRes.data.allocations || [];
      const conflicts = conflictsRes.data.conflicts || [];
      
      // Calculate average utilisation
      const totalUtil = roles.reduce((acc, r) => acc + (r.utilisation?.utilisation_percent || 0), 0);
      const avgUtil = roles.length > 0 ? Math.round(totalUtil / roles.length) : 0;
      
      setStats({
        roles: roles.length,
        teams: teams.length,
        allocations: allocations.length,
        avgUtilisation: avgUtil,
        conflicts: conflicts.length
      });
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const resourceModules = [
    {
      title: 'Resource Explorer',
      description: 'Browse and manage all roles, teams, and their allocations in one place.',
      icon: Users,
      path: '/resources/explorer',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      title: 'Capacity Management',
      description: 'Configure capacity calendars, working hours, and availability for resources.',
      icon: Calendar,
      path: '/resources/capacity',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Utilisation Heatmap',
      description: 'Visual heatmap view of resource allocation across time periods.',
      icon: BarChart3,
      path: '/resources/utilisation',
      color: 'bg-green-100 text-green-600'
    },
    {
      title: 'Portfolio Capacity',
      description: 'Compare total capacity vs demand across all projects in the portfolio.',
      icon: PieChart,
      path: '/resources/portfolio',
      color: 'bg-amber-100 text-amber-600'
    }
  ];

  return (
    <PageErrorBoundary>
      <div className="min-h-screen bg-gray-50 p-6" data-testid="resource-landing-page">
        {/* Demo Data Banner */}
        {isDemoTenant && stats.roles > 0 && (
          <div className="mb-4 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2" data-testid="demo-data-banner">
            <Database className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-700 font-medium">Demo Data Active</span>
            <span className="text-xs text-amber-600">(Sample roles, teams, and allocations for demonstration)</span>
          </div>
        )}

        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Resource Management</h1>
            <p className="mt-2 text-gray-500">
              Plan capacity, track utilisation, and manage your team allocations across the portfolio.
            </p>
          </div>
          
          {/* AI Advisor Toggle (Coming Soon) */}
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg opacity-60 cursor-not-allowed" data-testid="ai-advisor-toggle">
            <Bot className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-500">AI Resource Advisor</span>
            <span className="text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">Coming Soon</span>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <UserCircle className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : stats.roles}
                </p>
                <p className="text-sm text-gray-500">Active Roles</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : stats.teams}
                </p>
                <p className="text-sm text-gray-500">Teams</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Briefcase className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : stats.allocations}
                </p>
                <p className="text-sm text-gray-500">Allocations</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <BarChart3 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : `${stats.avgUtilisation}%`}
                </p>
                <p className="text-sm text-gray-500">Avg Utilisation</p>
              </div>
            </div>
          </div>
          <div className={`bg-white rounded-xl border p-4 ${stats.conflicts > 0 ? 'border-red-200 bg-red-50' : ''}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${stats.conflicts > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                <AlertTriangle className={`w-5 h-5 ${stats.conflicts > 0 ? 'text-red-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className={`text-2xl font-bold ${stats.conflicts > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : stats.conflicts}
                </p>
                <p className="text-sm text-gray-500">Conflicts</p>
              </div>
            </div>
          </div>
        </div>

        {/* Module Cards */}
        <div className="grid grid-cols-2 gap-6">
          {resourceModules.map(module => (
            <ResourceCard key={module.path} {...module} />
          ))}
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white">
          <h2 className="text-xl font-semibold mb-2">Get Started</h2>
          <p className="text-purple-100 mb-4">
            New to resource management? Start by exploring your existing resources or setting up capacity calendars.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/resources/explorer')}
              className="flex items-center gap-2 px-4 py-2 bg-white text-purple-700 rounded-lg hover:bg-purple-50 font-medium"
              data-testid="explore-resources-btn"
            >
              <Users className="w-4 h-4" />
              Explore Resources
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/resources/capacity')}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-400 font-medium"
              data-testid="setup-capacity-btn"
            >
              <Calendar className="w-4 h-4" />
              Setup Capacity
            </button>
          </div>
        </div>
      </div>
    </PageErrorBoundary>
  );
};

export default ResourceLandingPage;
